/**
 * WelcomeBackScreen.tsx — OPERATOR IDENTITY™ Phase 3 (Build 2.08.0)
 *
 * Personalised boot experience for RETURNING operators (persona exists and
 * onboarding is complete). Replaces the generic loading gap between
 * authentication and Home:
 *
 *   Welcome back, {name} → Current/Home Base → module checklist ticks live
 *   as the Hydration Orchestrator restores each module → "Ready to Roam."
 *   → auto-transition into Home.
 *
 * Rules:
 *   • Never blocks the app: hard cap of 6s, then transitions regardless
 *     (hydration continues fire-and-forget in the background).
 *   • Shows "Offline Mode" honestly when the cloud is unreachable.
 *   • All values come from the operator profile + storage diagnostics —
 *     nothing fabricated.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Cloud, CloudOff, MapPin, Home, Loader2 } from 'lucide-react';
import { loadPersona } from '@/services/OperatorPersonaService';
import {
  hydrationOrchestrator,
  getLastRoute,
  type HydrationReport,
} from '@/services/HydrationOrchestrator';

const T = {
  bg:      '#030d1f',
  text:    '#f1f5f9',
  muted:   'rgba(255,255,255,0.48)',
  dim:     'rgba(255,255,255,0.26)',
  accent:  '#3b82f6',
  success: '#22c55e',
  warn:    '#f59e0b',
  mono:    'monospace' as const,
} as const;

const MAX_SCREEN_MS   = 6000; // hard cap — never hold the operator hostage
const DONE_LINGER_MS  = 900;  // brief "Ready to Roam." moment before transition

const CSS = `
  @keyframes wbs-fade-in { from { opacity:0; } to { opacity:1; } }
  @keyframes wbs-fade-up { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes wbs-fade-out { from { opacity:1; } to { opacity:0; } }
  @keyframes wbs-spin { to { transform:rotate(360deg); } }
  .wbs-root       { animation: wbs-fade-in 0.3s ease both; }
  .wbs-root.done  { animation: wbs-fade-out 0.45s ease both; }
  .wbs-block      { animation: wbs-fade-up 0.4s ease both; }
  .wbs-spin       { animation: wbs-spin 1s linear infinite; }
`;

function StepIcon({ status }: { status: string }) {
  if (status === 'restored' || status === 'no-data' || status === 'skipped') {
    return <Check style={{ width: 13, height: 13, color: T.success }} />;
  }
  if (status === 'failed') {
    return <X style={{ width: 13, height: 13, color: T.warn }} />;
  }
  if (status === 'running') {
    return <Loader2 className="wbs-spin" style={{ width: 13, height: 13, color: T.accent }} />;
  }
  return <div style={{ width: 6, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.14)', margin: 3.5 }} />;
}

export default function WelcomeBackScreen({ onDone }: { onDone: () => void }) {
  // SSOT fix (Task 95 + Task 98):
  //
  // The persona is React state (not a frozen useRef) so the display updates
  // when new data arrives. We re-read from localStorage at two points:
  //
  //   1. As soon as the 'profile' hydration step (step 1 of 16) reaches a
  //      terminal state — that step calls hydratePersonaFromCloud() and writes
  //      the authoritative cloud persona into localStorage. This is the earliest
  //      moment the correct Home Base is available.  Without this, the screen
  //      showed the stale pre-boot value (e.g. "Pai") for all 16 steps even
  //      though the correct value ("Phoenix") arrived after step 1.
  //
  //   2. On completedAt (all 16 steps done) as a safety net — covers the case
  //      where profile step arrived with no-data but a later step updated it.
  //
  // Never use raw.value / 30 or any other fallback — always call loadPersona().
  const [persona, setPersona] = useState(() => loadPersona());
  const [report, setReport] = useState<HydrationReport>(() => hydrationOrchestrator.getReport());
  const [phase, setPhase]   = useState<'restoring' | 'ready' | 'exiting'>('restoring');
  const doneRef = useRef(false);
  // Track whether the profile step result has already been consumed so we only
  // re-read persona once when it first becomes terminal (idempotent guard).
  const profileReadRef = useRef(false);

  useEffect(() => {
    const unsub = hydrationOrchestrator.subscribe((newReport) => {
      setReport(newReport);

      // ── Re-read after profile step becomes terminal (earliest correct data) ──
      // The 'profile' step (id: 'profile') runs hydratePersonaFromCloud().
      // Once it is restored/no-data/failed the localStorage persona is as
      // up-to-date as the cloud can make it — use it immediately.
      if (!profileReadRef.current) {
        const profileStep = newReport.steps.find(s => s.id === 'profile');
        const isTerminal  = profileStep && (
          profileStep.status === 'restored' ||
          profileStep.status === 'no-data'  ||
          profileStep.status === 'failed'
        );
        if (isTerminal) {
          profileReadRef.current = true;
          setPersona(loadPersona());
        }
      }

      // ── Safety-net re-read on full completion ────────────────────────────────
      if (newReport.completedAt) {
        setPersona(loadPersona());
      }
    });
    // Kick (or join) the pipeline — idempotent per boot.
    void hydrationOrchestrator.runFullHydration();
    return unsub;
  }, []);

  // Transition to "Ready to Roam." when hydration completes — or at the cap.
  useEffect(() => {
    if (doneRef.current) return;
    if (report.completedAt && phase === 'restoring') {
      setPhase('ready');
    }
  }, [report.completedAt, phase]);

  useEffect(() => {
    const cap = setTimeout(() => { if (!doneRef.current) setPhase('ready'); }, MAX_SCREEN_MS);
    return () => clearTimeout(cap);
  }, []);

  useEffect(() => {
    if (phase !== 'ready' || doneRef.current) return;
    const t = setTimeout(() => {
      doneRef.current = true;
      setPhase('exiting');
      setTimeout(onDone, 460);
    }, DONE_LINGER_MS);
    return () => clearTimeout(t);
  }, [phase, onDone]);

  const firstName = (persona?.nickname ?? persona?.displayName ?? 'Roamer').trim().split(/\s+/)[0];
  const dailyGoal = persona?.dailyGoalUSD ?? 0;
  const resumeRoute = getLastRoute();
  const offline = report.source === 'offline';
  const cloudSteps = report.steps.filter(s => s.status !== 'skipped');
  const finished = cloudSteps.filter(s => s.status === 'restored' || s.status === 'no-data' || s.status === 'failed').length;

  const body = (
    <div
      className={`wbs-root${phase === 'exiting' ? ' done' : ''}`}
      style={{
        position: 'fixed', inset: 0, zIndex: 99998,
        background: T.bg,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <style>{CSS}</style>

      <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 32 }}>

        {/* ── Greeting ─────────────────────────────────────────────────── */}
        <div className="wbs-block" style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.28em', color: 'rgba(96,165,250,0.65)', marginBottom: 10 }}>
            WELCOME BACK
          </div>
          <h1 style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 34, color: T.text, margin: 0, lineHeight: 1.1 }}>
            {firstName}
          </h1>
        </div>

        {/* ── Bases ────────────────────────────────────────────────────── */}
        {(persona?.currentBase || persona?.homeBase) && (
          <div className="wbs-block" style={{ display: 'flex', gap: 10, justifyContent: 'center', animationDelay: '0.12s' }}>
            {persona?.currentBase && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                borderRadius: 12, background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)',
              }}>
                <MapPin style={{ width: 13, height: 13, color: T.accent }} />
                <div>
                  <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '0.16em', color: T.dim }}>CURRENT BASE</div>
                  <div style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 700, color: T.text }}>{persona.currentBase.city}</div>
                </div>
              </div>
            )}
            {persona?.homeBase && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <Home style={{ width: 13, height: 13, color: T.muted }} />
                <div>
                  <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '0.16em', color: T.dim }}>HOME BASE</div>
                  <div style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 700, color: T.text }}>{persona.homeBase.city}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Mission (Phase 5) ────────────────────────────────────────── */}
        {dailyGoal > 0 && (
          <div className="wbs-block" style={{ display: 'flex', justifyContent: 'center', animationDelay: '0.18s' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px',
              borderRadius: 999, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.28)',
            }}>
              <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '0.16em', color: T.dim }}>MISSION</span>
              <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 800, color: T.success }}>
                EARN ${dailyGoal} TODAY
              </span>
            </div>
          </div>
        )}

        {/* ── Sync status ──────────────────────────────────────────────── */}
        <div className="wbs-block" style={{ animationDelay: '0.22s' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginBottom: 14,
          }}>
            {offline
              ? <CloudOff style={{ width: 13, height: 13, color: T.warn }} />
              : <Cloud style={{ width: 13, height: 13, color: T.accent }} />}
            <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: offline ? T.warn : T.muted }}>
              {phase !== 'restoring'
                ? (offline ? 'OFFLINE MODE — LOCAL WORKSPACE RESTORED' : 'WORKSPACE SYNCHRONIZED')
                : (offline ? 'OFFLINE MODE' : `SYNCHRONIZING… ${finished}/${cloudSteps.length}`)}
            </span>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px',
            padding: '16px 20px', borderRadius: 14,
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
          }}>
            {report.steps.filter(s => s.status !== 'skipped').map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StepIcon status={s.status} />
                <span style={{
                  fontFamily: T.mono, fontSize: 10.5, fontWeight: 600,
                  color: s.status === 'pending' ? T.dim : s.status === 'failed' ? T.warn : T.muted,
                }}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Ready ────────────────────────────────────────────────────── */}
        {phase !== 'restoring' && (
          <div className="wbs-block" style={{ textAlign: 'center' }}>
            <span style={{ fontFamily: T.mono, fontSize: 15, fontWeight: 900, letterSpacing: '0.14em', color: T.success }}>
              READY TO ROAM.
            </span>
            {resumeRoute && resumeRoute !== '/home' && (
              <div style={{ fontFamily: T.mono, fontSize: 10, letterSpacing: '0.14em', color: T.muted, marginTop: 8 }}>
                RESUMING → {resumeRoute.toUpperCase()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(body, document.body);
}
